import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Wrench,
  CheckCircle,
  Warning,
  X,
  QrCode,
  Printer,
  Storefront,
  User,
  ClockCounterClockwise,
  ArrowsClockwise,
  BatteryHigh,
  PencilSimple,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import QRScanner from "../components/QRScanner";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const DEFAULT_REVISION_LABELS = {
  inspecao_visual: "Inspeção visual",
  teste_motor: "Teste do motor",
  teste_freios: "Teste dos freios",
  teste_luzes: "Teste das luzes",
  teste_bateria: "Teste da bateria",
  teste_painel: "Teste do painel",
  limpeza_geral: "Limpeza geral",
  aprovado_qualidade: "Aprovado qualidade",
};

export default function RevisionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [moto, setMoto] = useState(null);
  const [revision, setRevision] = useState(null);
  const [stores, setStores] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);
  const [checklistLabels, setChecklistLabels] = useState(
    DEFAULT_REVISION_LABELS,
  );

  // Form for starting revision
  const [showStartForm, setShowStartForm] = useState(false);
  const [formData, setFormData] = useState({
    store_id: "",
    technician_id: "",
    checklist_template_id: "",
    notes: "",
  });

  // Part replacement state
  const [replacingItem, setReplacingItem] = useState(null); // checklist key being replaced
  const [partDescription, setPartDescription] = useState("");
  const [replacingBattery, setReplacingBattery] = useState(null); // old battery SN
  const [newBatterySN, setNewBatterySN] = useState("");
  const [showBatteryScanner, setShowBatteryScanner] = useState(false);
  const [submittingPart, setSubmittingPart] = useState(false);

  // Finalize state
  const [showFinalizeForm, setShowFinalizeForm] = useState(false);
  const [nextRevisionDate, setNextRevisionDate] = useState("");
  const [finalizing, setFinalizing] = useState(false);

  const fetchMoto = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/motorcycles/${id}`, {
        withCredentials: true,
      });
      setMoto(response.data);

      if (response.data.status === "revision") {
        const revResponse = await axios.get(
          `${API}/motorcycles/${id}/revision`,
          { withCredentials: true },
        );
        setRevision(revResponse.data);

        if (revResponse.data.checklist_template_id) {
          try {
            const templateRes = await axios.get(
              `${API}/checklist-templates/${revResponse.data.checklist_template_id}`,
              { withCredentials: true },
            );
            const labels = {};
            Object.entries(templateRes.data.items).forEach(([key, item]) => {
              labels[key] = item.label;
            });
            setChecklistLabels(labels);
          } catch (err) {
            console.error("Error fetching template:", err);
          }
        }
      }
    } catch (error) {
      toast.error("Erro ao carregar dados da moto");
      navigate("/consulta");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchOptions = async () => {
    try {
      const [storesRes, techsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/stores`, { withCredentials: true }),
        axios.get(`${API}/technicians`, { withCredentials: true }),
        axios.get(`${API}/checklist-templates`, { withCredentials: true }),
      ]);
      setStores(storesRes.data);
      setTechnicians(techsRes.data);
      setChecklistTemplates(templatesRes.data);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  useEffect(() => {
    fetchMoto();
    fetchOptions();
  }, [fetchMoto]);

  useEffect(() => {
    if (formData.store_id) {
      fetchTechnicians(formData.store_id);
    }
  }, [formData.store_id]);

  const fetchTechnicians = async (storeId) => {
    try {
      const response = await axios.get(
        `${API}/technicians?store_id=${storeId}`,
        { withCredentials: true },
      );
      setTechnicians(response.data);
    } catch (error) {
      console.error("Error fetching technicians:", error);
    }
  };

  const handleStartRevision = async () => {
    try {
      const data = {
        store_id: formData.store_id || null,
        technician_id: formData.technician_id || null,
        checklist_template_id: formData.checklist_template_id || null,
        notes: formData.notes || null,
      };
      await axios.post(`${API}/motorcycles/${id}/revision`, data, {
        withCredentials: true,
      });
      toast.success("Revisão iniciada com sucesso!");
      setShowStartForm(false);
      fetchMoto();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao iniciar revisão");
    }
  };

  const handleChecklistChange = async (item, checked) => {
    try {
      await axios.put(
        `${API}/motorcycles/${id}/revision/checklist`,
        { item, checked },
        { withCredentials: true },
      );
      setRevision((prev) => ({
        ...prev,
        checklist: { ...prev.checklist, [item]: checked },
      }));
    } catch (error) {
      toast.error("Erro ao atualizar checklist");
    }
  };

  const handleReplacePart = async (checklistItem) => {
    if (!partDescription.trim()) {
      toast.error("Descreva a peça substituída");
      return;
    }
    setSubmittingPart(true);
    try {
      await axios.post(
        `${API}/motorcycles/${id}/revision/replace-part`,
        {
          checklist_item: checklistItem,
          description: partDescription.trim(),
        },
        { withCredentials: true },
      );
      toast.success("Substituição registrada!");
      setReplacingItem(null);
      setPartDescription("");
      fetchMoto();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Erro ao registrar substituição",
      );
    } finally {
      setSubmittingPart(false);
    }
  };

  const handleReplaceBattery = async () => {
    if (!newBatterySN.trim()) {
      toast.error("Digite ou escaneie o SN da nova bateria");
      return;
    }
    setSubmittingPart(true);
    try {
      const resp = await axios.post(
        `${API}/motorcycles/${id}/revision/replace-battery`,
        {
          old_battery: replacingBattery,
          new_battery: newBatterySN.trim().toUpperCase(),
        },
        { withCredentials: true },
      );
      toast.success(resp.data.message);
      setReplacingBattery(null);
      setNewBatterySN("");
      fetchMoto();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao substituir bateria");
    } finally {
      setSubmittingPart(false);
    }
  };

  const handleBatteryScan = (code) => {
    setNewBatterySN(code.trim().toUpperCase());
    setShowBatteryScanner(false);
  };

  const handleFinalizeRevision = async () => {
    if (!nextRevisionDate) {
      toast.error("Selecione a data da próxima revisão");
      return;
    }
    setFinalizing(true);
    try {
      await axios.post(
        `${API}/motorcycles/${id}/revision/finalize`,
        {
          next_revision_date: new Date(
            nextRevisionDate + "T12:00:00Z",
          ).toISOString(),
        },
        { withCredentials: true },
      );
      toast.success("Revisão finalizada com sucesso!");
      setShowFinalizeForm(false);
      fetchMoto();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao finalizar revisão");
    } finally {
      setFinalizing(false);
    }
  };

  const setQuickDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setNextRevisionDate(date.toISOString().split("T")[0]);
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Montagem Finalizada";
      case "revision":
        return "Em Revisão";
      case "revised":
        return "Revisão Concluída";
      case "pending":
        return "Pendente";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "text-[#00E676]";
      case "revision":
        return "text-yellow-500";
      case "revised":
        return "text-blue-500";
      case "pending":
        return "text-[#A1A1AA]";
      default:
        return "text-[#A1A1AA]";
    }
  };

  const isBatteryItem = (key) =>
    key.includes("bateria") || key.includes("battery");

  const allChecklistDone = revision
    ? Object.values(revision.checklist).every((v) => v)
    : false;
  const canFinalize = moto?.status === "revision" && allChecklistDone;
  const partsReplaced = revision?.parts_replaced || [];

  if (loading) {
    return (
      <Layout title="Revisão" showBack backTo="/consulta">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  if (!moto) return null;

  return (
    <Layout title={`Revisão - ${moto.chassis_sn}`} showBack backTo="/consulta">
      <div className="space-y-4 pb-6">
        {/* Moto Info Header */}
        <div className="card p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-[#A1A1AA]">Modelo</p>
              <p className="text-base sm:text-lg font-bold truncate">
                {moto.model}
              </p>
            </div>
            <div className="sm:text-right shrink-0 border-t border-white/5 sm:border-0 pt-2 sm:pt-0">
              <p className="text-xs sm:text-sm text-[#A1A1AA]">Voltagem</p>
              <p className="text-base sm:text-lg font-bold">{moto.voltage}V</p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-white/10">
            <div className="flex items-center gap-2">
              <Wrench size={20} className={getStatusColor(moto.status)} />
              <span
                className={`font-medium text-sm sm:text-base ${getStatusColor(moto.status)}`}
              >
                {getStatusText(moto.status)}
              </span>
            </div>
            {moto.revision_count > 0 && (
              <span className="text-xs sm:text-sm text-[#A1A1AA] font-medium">
                {moto.revision_count} revisão(ões)
              </span>
            )}
          </div>
        </div>

        {/* Start Revision Button */}
        {(moto.status === "completed" || moto.status === "revised") &&
          !showStartForm && (
            <button
              onClick={() => setShowStartForm(true)}
              className="btn-primary w-full flex items-center justify-center gap-2 py-4 text-base font-bold shadow-lg"
              data-testid="start-revision-button"
            >
              <Wrench size={20} />
              Iniciar Nova Revisão
            </button>
          )}

        {/* Start Revision Form */}
        {showStartForm && (
          <div className="card p-4 sm:p-5 space-y-4 border border-[#00E676]/30">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-lg">Iniciar Revisão</h3>
              <button
                onClick={() => setShowStartForm(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[#1A1A1A] transition-colors"
              >
                <X size={20} className="text-[#A1A1AA]" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs sm:text-sm text-[#A1A1AA] block mb-1.5 ml-1">
                  Loja
                </label>
                <select
                  value={formData.store_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      store_id: e.target.value,
                      technician_id: "",
                    })
                  }
                  className="input-field h-12 w-full truncate"
                  data-testid="revision-store-select"
                >
                  <option value="">Selecione a loja</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name} {store.city ? `- ${store.city}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs sm:text-sm text-[#A1A1AA] block mb-1.5 ml-1">
                  Técnico
                </label>
                <select
                  value={formData.technician_id}
                  onChange={(e) =>
                    setFormData({ ...formData, technician_id: e.target.value })
                  }
                  className="input-field h-12 w-full truncate"
                  data-testid="revision-tech-select"
                >
                  <option value="">Selecione o técnico</option>
                  {technicians.map((tech) => (
                    <option key={tech.id} value={tech.id}>
                      {tech.name}{" "}
                      {tech.store_name ? `(${tech.store_name})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs sm:text-sm text-[#A1A1AA] block mb-1.5 ml-1">
                  Template de Checklist
                </label>
                <select
                  value={formData.checklist_template_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      checklist_template_id: e.target.value,
                    })
                  }
                  className="input-field h-12 w-full truncate"
                  data-testid="revision-template-select"
                >
                  <option value="">Checklist padrão de revisão</option>
                  {checklistTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs sm:text-sm text-[#A1A1AA] block mb-1.5 ml-1">
                  Observações
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  className="input-field h-24 resize-none py-3 w-full text-sm"
                  placeholder="Motivo da revisão, observações iniciais..."
                  data-testid="revision-notes-input"
                />
              </div>
              <button
                onClick={handleStartRevision}
                className="btn-primary w-full py-4 font-bold"
                data-testid="confirm-start-revision"
              >
                Confirmar e Iniciar Revisão
              </button>
            </div>
          </div>
        )}

        {/* Revision in Progress */}
        {moto.status === "revision" && revision && (
          <>
            {/* Revision Info */}
            <div className="card p-4 sm:p-5">
              <h3 className="font-bold mb-3 text-base sm:text-lg">
                Revisão #{revision.revision_number}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
                {revision.store_name && (
                  <div className="flex items-center gap-2 min-w-0">
                    <Storefront
                      size={16}
                      className="text-purple-500 shrink-0"
                    />
                    <span className="text-[#A1A1AA] truncate">
                      {revision.store_name}
                    </span>
                  </div>
                )}
                {revision.technician_name && (
                  <div className="flex items-center gap-2 min-w-0">
                    <User size={16} className="text-blue-500 shrink-0" />
                    <span className="text-[#A1A1AA] truncate">
                      {revision.technician_name}
                    </span>
                  </div>
                )}
              </div>
              {revision.notes && (
                <div className="mt-4 pt-3 border-t border-white/10">
                  <p className="text-xs text-[#A1A1AA] mb-1 font-medium">
                    Observações Iniciais:
                  </p>
                  <p className="text-xs sm:text-sm text-white leading-relaxed bg-[#1A1A1A] p-3 rounded-lg border border-white/5">
                    {revision.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Revision Checklist */}
            <div className="card p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-sm sm:text-base">
                  Checklist de Revisão
                </h3>
                <span
                  className={`text-sm sm:text-base ${allChecklistDone ? "text-[#00E676]" : "text-[#A1A1AA]"}`}
                >
                  {Object.values(revision.checklist).filter((v) => v).length}/
                  {Object.keys(revision.checklist).length}
                </span>
              </div>

              <div className="space-y-3">
                {Object.entries(revision.checklist).map(([key, checked]) => (
                  <div
                    key={key}
                    className="bg-[#1A1A1A] rounded-lg p-3 sm:p-4 border border-transparent hover:border-white/5 transition-colors"
                    data-testid={`revision-checklist-${key}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <span className="text-sm font-medium flex-1 pr-2 leading-snug">
                        {checklistLabels[key] ||
                          key
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                      <div className="flex items-center gap-2 self-end sm:self-auto w-full sm:w-auto">
                        <button
                          onClick={() => handleChecklistChange(key, false)}
                          className={`flex-1 sm:flex-none px-4 py-2 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                            !checked
                              ? "bg-[#FF3B30] text-white"
                              : "bg-[#252525] text-[#A1A1AA] hover:bg-[#303030]"
                          }`}
                        >
                          NÃO
                        </button>
                        <button
                          onClick={() => handleChecklistChange(key, true)}
                          className={`flex-1 sm:flex-none px-4 py-2 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                            checked
                              ? "bg-[#00E676] text-black"
                              : "bg-[#252525] text-[#A1A1AA] hover:bg-[#303030]"
                          }`}
                        >
                          SIM
                        </button>
                      </div>
                    </div>

                    {/* Replace Part Button / Form */}
                    <div className="mt-3 pt-3 border-t border-white/5">
                      {replacingItem === key ? (
                        <div className="space-y-3">
                          {isBatteryItem(key) ? (
                            /* Battery replacement - show battery list */
                            <div className="space-y-3 bg-[#252525] p-3 rounded-lg border border-orange-500/20">
                              <p className="text-xs text-orange-400 font-bold uppercase tracking-wider">
                                Selecione a bateria atual:
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {moto.batteries.map((bat) => (
                                  <div
                                    key={bat}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#1A1A1A] border border-white/5 rounded-lg p-2 gap-2"
                                  >
                                    <div className="flex items-center gap-2 min-w-0 px-1">
                                      <BatteryHigh
                                        size={16}
                                        className="text-[#00E676] shrink-0"
                                      />
                                      <span className="text-xs font-mono truncate">
                                        {bat}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setReplacingBattery(bat);
                                        setNewBatterySN("");
                                      }}
                                      className={`text-xs px-3 py-2 sm:py-1 rounded-md transition-colors w-full sm:w-auto ${
                                        replacingBattery === bat
                                          ? "bg-orange-500 text-white shadow-md"
                                          : "bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/20"
                                      }`}
                                      data-testid={`replace-battery-${bat}`}
                                    >
                                      Substituir
                                    </button>
                                  </div>
                                ))}
                              </div>

                              {/* New battery input */}
                              {replacingBattery && (
                                <div className="bg-[#1A1A1A] rounded-lg p-3 space-y-3 mt-3 border border-orange-500/30">
                                  <p className="text-[11px] sm:text-xs text-[#A1A1AA]">
                                    Substituindo SN:{" "}
                                    <span className="text-orange-400 font-mono font-bold break-all">
                                      {replacingBattery}
                                    </span>
                                  </p>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={newBatterySN}
                                      onChange={(e) =>
                                        setNewBatterySN(
                                          e.target.value.toUpperCase(),
                                        )
                                      }
                                      placeholder="SN nova bateria"
                                      className="input-field flex-1 h-12 text-sm font-mono w-full"
                                      data-testid="new-battery-sn-input"
                                    />
                                    <button
                                      onClick={() =>
                                        setShowBatteryScanner(true)
                                      }
                                      className="w-12 h-12 rounded-lg bg-[#00E676]/10 flex items-center justify-center shrink-0 hover:bg-[#00E676]/20 transition-colors"
                                      data-testid="scan-new-battery-btn"
                                    >
                                      <QrCode
                                        size={20}
                                        className="text-[#00E676]"
                                      />
                                    </button>
                                  </div>
                                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                                    <button
                                      onClick={() => {
                                        setReplacingBattery(null);
                                        setNewBatterySN("");
                                      }}
                                      className="btn-secondary text-sm h-10 w-full sm:flex-1"
                                    >
                                      Cancelar
                                    </button>
                                    <button
                                      onClick={handleReplaceBattery}
                                      disabled={
                                        !newBatterySN.trim() || submittingPart
                                      }
                                      className="btn-primary text-sm h-10 w-full sm:flex-1 font-bold"
                                      data-testid="confirm-replace-battery"
                                    >
                                      {submittingPart
                                        ? "Substituindo..."
                                        : "Confirmar Troca"}
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Also allow general part description for battery if manual note needed */}
                              <div className="mt-4 pt-3 border-t border-white/5">
                                <p className="text-[10px] sm:text-xs text-[#A1A1AA] mb-2 font-medium">
                                  Ou registre nota manual (não atualiza o
                                  sistema de rastreio):
                                </p>
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <input
                                    type="text"
                                    value={partDescription}
                                    onChange={(e) =>
                                      setPartDescription(e.target.value)
                                    }
                                    placeholder="Ex: Troca de conector da bateria..."
                                    className="input-field flex-1 h-10 text-sm w-full"
                                    data-testid="part-description-input"
                                  />
                                  <button
                                    onClick={() => handleReplacePart(key)}
                                    disabled={
                                      !partDescription.trim() || submittingPart
                                    }
                                    className="btn-primary text-sm h-10 px-6 w-full sm:w-auto"
                                    data-testid="confirm-replace-part"
                                  >
                                    Salvar Nota
                                  </button>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setReplacingItem(null);
                                  setReplacingBattery(null);
                                  setPartDescription("");
                                }}
                                className="w-full text-center text-xs text-[#A1A1AA] hover:text-white mt-2 py-2"
                              >
                                Fechar
                              </button>
                            </div>
                          ) : (
                            /* Non-battery part replacement */
                            <div className="bg-[#252525] p-3 sm:p-4 rounded-lg space-y-3 border border-orange-500/20">
                              <p className="text-xs text-orange-400 font-bold uppercase tracking-wider">
                                Registrar Reparo / Troca de Peça
                              </p>
                              <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                  type="text"
                                  value={partDescription}
                                  onChange={(e) =>
                                    setPartDescription(e.target.value)
                                  }
                                  placeholder="Descreva a peça substituída ou reparo..."
                                  className="input-field flex-1 h-10 sm:h-12 text-sm w-full"
                                  data-testid="part-description-input"
                                />
                                <button
                                  onClick={() => handleReplacePart(key)}
                                  disabled={
                                    !partDescription.trim() || submittingPart
                                  }
                                  className="btn-primary text-sm h-10 sm:h-12 px-6 w-full sm:w-auto font-bold"
                                  data-testid="confirm-replace-part"
                                >
                                  {submittingPart ? "..." : "Salvar Registro"}
                                </button>
                              </div>
                              <button
                                onClick={() => {
                                  setReplacingItem(null);
                                  setPartDescription("");
                                }}
                                className="w-full text-center text-xs text-[#A1A1AA] hover:text-white pt-1"
                              >
                                Cancelar
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setReplacingItem(key);
                            setPartDescription("");
                            setReplacingBattery(null);
                            setNewBatterySN("");
                          }}
                          className="w-full sm:w-auto text-xs px-3 py-2 sm:py-1.5 rounded-lg bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/20 flex items-center justify-center gap-1.5 transition-colors font-medium"
                          data-testid={`replace-part-${key}`}
                        >
                          <ArrowsClockwise size={14} />
                          Registrar Reparo ou Troca
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Parts Replaced Summary */}
            {partsReplaced.length > 0 && (
              <div className="card p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/10">
                  <ArrowsClockwise size={20} className="text-orange-400" />
                  <h3 className="font-bold text-sm sm:text-base">
                    Peças Substituídas / Reparos ({partsReplaced.length})
                  </h3>
                </div>
                <div className="space-y-2">
                  {partsReplaced.map((part, idx) => (
                    <div
                      key={idx}
                      className="bg-[#1A1A1A] rounded-lg p-3 sm:p-4 text-sm border border-white/5 hover:border-orange-500/20 transition-colors"
                      data-testid={`replaced-part-${idx}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                        <div className="min-w-0">
                          <p className="text-orange-400 font-bold text-xs sm:text-sm truncate">
                            {checklistLabels[part.checklist_item] ||
                              part.checklist_item}
                          </p>
                          <p className="text-[#A1A1AA] mt-1 text-sm leading-relaxed">
                            {part.description}
                          </p>
                        </div>
                        <span className="text-[10px] sm:text-xs text-[#A1A1AA] whitespace-nowrap self-start bg-[#252525] px-2 py-1 rounded-md font-medium">
                          {part.replaced_by}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Finalize Revision */}
            <div className="space-y-3">
              {!canFinalize && (
                <div className="card p-4 border-yellow-500/30 bg-yellow-500/5">
                  <div className="flex items-start gap-3">
                    <Warning
                      size={20}
                      className="text-yellow-500 flex-shrink-0 mt-0.5"
                    />
                    <div className="text-xs sm:text-sm min-w-0">
                      <p className="font-bold text-yellow-500 mb-1">
                        Para finalizar:
                      </p>
                      <p className="text-[#A1A1AA]">
                        Complete todos os itens do checklist de revisão
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!showFinalizeForm ? (
                <button
                  onClick={() => setShowFinalizeForm(true)}
                  disabled={!canFinalize}
                  className="btn-primary w-full py-4 text-base font-bold shadow-lg"
                  data-testid="finalize-revision-button"
                >
                  Finalizar Revisão
                </button>
              ) : (
                <div
                  className="card p-4 sm:p-5 space-y-4 border-[#00E676]/30 shadow-[0_0_20px_rgba(0,230,118,0.1)]"
                  data-testid="finalize-form"
                >
                  <h3 className="font-bold text-lg">Finalizar Revisão</h3>
                  <div>
                    <label className="text-xs sm:text-sm text-[#A1A1AA] block mb-2 ml-1 font-medium">
                      Data da Próxima Revisão *
                    </label>
                    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-3">
                      {[
                        { days: 45, label: "45 dias" },
                        { days: 60, label: "60 dias" },
                        { days: 90, label: "90 dias" },
                      ].map((opt) => (
                        <button
                          key={opt.days}
                          type="button"
                          onClick={() => setQuickDate(opt.days)}
                          className={`text-xs sm:text-sm px-4 py-2.5 sm:py-2 rounded-lg border transition-colors font-medium ${
                            (() => {
                              const target = new Date();
                              target.setDate(target.getDate() + opt.days);
                              return (
                                nextRevisionDate ===
                                target.toISOString().split("T")[0]
                              );
                            })()
                              ? "border-[#00E676] text-[#00E676] bg-[#00E676]/10 shadow-inner"
                              : "border-white/10 text-[#A1A1AA] hover:bg-[#1A1A1A]"
                          }`}
                          data-testid={`quick-date-${opt.days}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="date"
                      value={nextRevisionDate}
                      onChange={(e) => setNextRevisionDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="input-field h-12 w-full font-mono text-sm"
                      data-testid="next-revision-date-input"
                    />
                    {nextRevisionDate && (
                      <p className="text-xs sm:text-sm text-[#00E676] mt-2 ml-1 font-medium">
                        A próxima revisão será em:{" "}
                        {new Date(
                          nextRevisionDate + "T12:00:00",
                        ).toLocaleDateString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <button
                      onClick={() => setShowFinalizeForm(false)}
                      className="btn-secondary w-full sm:w-auto h-12 px-6"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleFinalizeRevision}
                      disabled={!nextRevisionDate || finalizing}
                      className="btn-primary w-full sm:flex-1 h-12 font-bold"
                      data-testid="confirm-finalize-button"
                    >
                      {finalizing ? "Finalizando..." : "Confirmar e Finalizar"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Revised - Show QR Code */}
        {moto.status === "revised" && (
          <div className="space-y-3 sm:space-y-4">
            <div className="card p-4 sm:p-5 border-blue-500/30 bg-blue-500/5">
              <div className="flex items-center gap-3 min-w-0">
                <CheckCircle
                  size={28}
                  weight="fill"
                  className="text-blue-500 shrink-0"
                />
                <div className="min-w-0">
                  <p className="font-bold text-blue-500 text-sm sm:text-base">
                    Revisão Concluída
                  </p>
                  <p className="text-xs sm:text-sm text-[#A1A1AA] mt-0.5 truncate">
                    {moto.last_revision_at &&
                      new Date(moto.last_revision_at).toLocaleDateString(
                        "pt-BR",
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <button
                onClick={() => setShowQRCode(true)}
                className="btn-secondary w-full flex items-center justify-center gap-2 py-3"
                data-testid="view-qrcode-button"
              >
                <QrCode size={20} />
                Ver QR Code
              </button>
              <button
                onClick={() => {
                  setShowQRCode(true);
                  setTimeout(() => window.print(), 500);
                }}
                className="btn-secondary w-full flex items-center justify-center gap-2 py-3 bg-[#1A1A1A] hover:bg-[#252525] border border-white/20"
                data-testid="print-label-button"
              >
                <Printer size={20} />
                Imprimir Etiqueta
              </button>
            </div>
          </div>
        )}

        {/* Revision History */}
        {moto.revision_count > 0 && (
          <div className="card p-4 sm:p-5 mt-6 border-dashed border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <ClockCounterClockwise size={20} className="text-[#A1A1AA]" />
              <h3 className="font-bold text-sm sm:text-base">
                Histórico de Revisões
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-[#A1A1AA] ml-7">
              Esta moto passou por{" "}
              <span className="text-white font-bold">
                {moto.revision_count}
              </span>{" "}
              revisão(ões) anterior(es).
            </p>
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-[#121212] rounded-xl p-5 sm:p-6 w-full max-w-sm shadow-2xl border border-white/10 my-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg">QR Code Pós-Revisão</h3>
              <button
                onClick={() => setShowQRCode(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#1A1A1A] transition-colors no-print"
                data-testid="close-qrcode-modal"
              >
                <X size={20} className="text-[#A1A1AA]" />
              </button>
            </div>

            <div className="print-label bg-white p-5 rounded-xl flex flex-col items-center shadow-inner">
              <img
                src={`${API}/motorcycles/${id}/qrcode`}
                alt="QR Code"
                className="w-48 h-48 sm:w-56 sm:h-56 object-contain"
                data-testid="qrcode-image"
              />
              <p className="chassis-text mt-3 text-black font-mono text-sm sm:text-base font-bold text-center break-all">
                {moto.chassis_sn}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-600 mt-1 font-medium text-center">
                {moto.model} - {moto.voltage}V
              </p>
              <p className="text-xs sm:text-sm text-blue-600 mt-2 font-bold tracking-widest border-t border-gray-200 pt-2 w-full text-center">
                REVISADO
              </p>
            </div>

            <p className="text-[11px] sm:text-xs text-[#A1A1AA] text-center mt-5 font-medium no-print">
              Escaneie para rastrear esta moto no sistema
            </p>
          </div>
        </div>
      )}

      {/* Battery QR Scanner Modal */}
      {showBatteryScanner && (
        <QRScanner
          onScan={handleBatteryScan}
          onClose={() => setShowBatteryScanner(false)}
        />
      )}
    </Layout>
  );
}
